import { useCallback, useMemo } from 'react';
import Swal, { SweetAlertOptions } from 'sweetalert2';
import { signIn } from 'next-auth/react';

import themeVariables from '@mais-retorno-front/theme/theme-variables';
import theme from '@mais-retorno-front/theme/theme';

export const useAlert = () => {
  const swalWithBootstrapButtons = useMemo(
    () =>
      Swal.mixin({
        confirmButtonText: 'Confirmar',
        cancelButtonText: 'Cancelar',
        denyButtonText: 'Sim, continuar',
        reverseButtons: true,
        confirmButtonColor: themeVariables.colors.primary,
        cancelButtonColor: themeVariables.colors.lightNeutral,
        denyButtonColor: themeVariables.colors.errorDark,
        customClass: {
          confirmButton: 'sweet-alert-confirm-button',
          cancelButton: 'sweet-alert-cancel-button',
          denyButton: 'sweet-alert-deny-button',
        },
      }),
    [Swal]
  );

  const Toast = useMemo(
    () =>
      Swal.mixin({
        toast: true,
        showConfirmButton: false,
        showCloseButton: true,
        width: '520px',
      }),
    [Swal]
  );

  const dispatchSuccessToast = useCallback(
    (options: SweetAlertOptions = {}) => {
      return Toast.fire({
        text: 'Alterações feitas com sucesso',
        background: '#75D191',
        color: '#272727',
        icon: 'success',
        iconColor: '#272727',
        position: 'bottom',
        timer: 3000,
        closeButtonHtml: "<span style='color: #272727'>&times;</span>",
        customClass: {
          htmlContainer: 'success-toast__container',
        },
        ...options,
      });
    },
    []
  );

  const dispatchWarningToast = useCallback(
    (options: SweetAlertOptions = {}) => {
      return Toast.fire({
        titleText: 'Alerta!',
        text: 'As alterações foram anuladas',
        background: '#DDAD54',
        color: '#272727',
        icon: 'warning',
        iconColor: '#272727',
        closeButtonHtml: "<span style='color: #272727'>&times;</span>",
        customClass: {
          title: 'warning-toast__title',
          htmlContainer: 'warning-toast__container',
        },
        ...options,
      });
    },
    []
  );

  const dispatchAlert = useCallback((options: SweetAlertOptions = {}) => {
    return swalWithBootstrapButtons.fire({ ...options });
  }, []);

  const dispatchLoadingAlert = useCallback(
    (options: SweetAlertOptions = {}) => {
      const callbackLoadingAlert = swalWithBootstrapButtons.fire({
        ...options,
        allowEscapeKey: false,
        allowOutsideClick: false,
        didOpen: () => {
          swalWithBootstrapButtons.showLoading(null);
        },
      });

      return { alert: swalWithBootstrapButtons, callbackLoadingAlert };
    },
    []
  );

  const dispatchSuccessAlert = useCallback(
    (options: SweetAlertOptions = {}) => {
      return swalWithBootstrapButtons.fire({
        icon: 'success',
        confirmButtonText: 'Entendi',
        ...options,
      });
    },
    []
  );

  const dispatchDangerAlert = useCallback((options: SweetAlertOptions = {}) => {
    return swalWithBootstrapButtons.fire({
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: themeVariables.colors.errorDark,
      reverseButtons: true,
      ...options,
    });
  }, []);

  const dispatchErrorAlert = useCallback((options: SweetAlertOptions = {}) => {
    return swalWithBootstrapButtons.fire({
      icon: 'error',
      confirmButtonText: 'Entendi',
      ...options,
    });
  }, []);

  const dispatchPrimeAlert = useCallback(
    (options: SweetAlertOptions = {}, primeLink: string) => {
      swalWithBootstrapButtons
        .fire({
          iconColor: '#FFE604',
          iconHtml:
            '<svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 576 512" color="#FFE604" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg" style="color: rgb(255, 230, 4);"><path d="M552 64H448V24c0-13.3-10.7-24-24-24H152c-13.3 0-24 10.7-24 24v40H24C10.7 64 0 74.7 0 88v56c0 35.7 22.5 72.4 61.9 100.7 31.5 22.7 69.8 37.1 110 41.7C203.3 338.5 240 360 240 360v72h-48c-35.3 0-64 20.7-64 56v12c0 6.6 5.4 12 12 12h296c6.6 0 12-5.4 12-12v-12c0-35.3-28.7-56-64-56h-48v-72s36.7-21.5 68.1-73.6c40.3-4.6 78.6-19 110-41.7 39.3-28.3 61.9-65 61.9-100.7V88c0-13.3-10.7-24-24-24zM99.3 192.8C74.9 175.2 64 155.6 64 144v-16h64.2c1 32.6 5.8 61.2 12.8 86.2-15.1-5.2-29.2-12.4-41.7-21.4zM512 144c0 16.1-17.7 36.1-35.3 48.8-12.5 9-26.7 16.2-41.8 21.4 7-25 11.8-53.6 12.8-86.2H512v16z"></path></svg>',
          title: 'Disponível apenas para assinantes!',
          confirmButtonText: 'Conheça o Retorno Prime',
          cancelButtonText: 'ver depois',
          cancelButtonColor: 'transparent',
          showCancelButton: true,
          background: 'linear-gradient(135deg, #39B8B9 0%, #1DDD58 100%)',
          color: theme.palette.primary.main,
          ...options,
        })
        .then(({ isConfirmed }) => {
          if (isConfirmed) {
            const newWindow = window.open(primeLink, 'blank');
            newWindow.opener = null;
          }
        });
    },
    []
  );

  const dispatchLoginAlert = useCallback(
    (text: string, redirectUri?: string, options: SweetAlertOptions = {}) => {
      // TODO: Implementação temporária - será removida quando todos os componentes migrarem
      return swalWithBootstrapButtons
        .fire({
          text,
          icon: 'info',
          title: 'Login necessário!',
          showCancelButton: true,
          cancelButtonText: 'Depois',
          confirmButtonText: 'Fazer login',
          ...options,
        })
        .then(({ isConfirmed }) => {
          if (isConfirmed) {
            signIn('keycloak', { callbackUrl: redirectUri });
          }
        });
    },
    []
  );

  const dispatchInfoAlert = useCallback((options: SweetAlertOptions = {}) => {
    return swalWithBootstrapButtons.fire({
      icon: 'info',
      confirmButtonText: 'Entendi',
      ...options,
    });
  }, []);

  return {
    dispatchAlert,
    dispatchErrorAlert,
    dispatchInfoAlert,
    dispatchDangerAlert,
    dispatchSuccessAlert,
    dispatchPrimeAlert,
    dispatchLoadingAlert,
    dispatchLoginAlert,
    dispatchSuccessToast,
    dispatchWarningToast,
  };
};
